import { createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import { 
  getTrainingLocations, 
  getLocationData,
  getAllTrainingCountries,
  LocationData
} from '../../services/location.service';

// Types
export interface LocationState {
  locations: LocationData[];
  uniqueCountries: string[];
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: LocationState = {
  locations: [],
  uniqueCountries: [],
  isLoading: false,
  error: null
};

// Async thunks
export const fetchUserLocations = createAsyncThunk<
  { locations: LocationData[]; allCountries: string[] },
  string,
  { rejectValue: string }
>('location/fetchUserLocations', async (userId, { rejectWithValue }) => {
  try {
    const [locations, allCountries] = await Promise.all([
      getTrainingLocations(userId),
      getAllTrainingCountries(userId)
    ]);
    return { locations, allCountries };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch training locations');
  }
});

export const fetchLocationData = createAsyncThunk<
  LocationData,
  string,
  { rejectValue: string }
>('location/fetchLocationData', async (placeId, { rejectWithValue }) => {
  try {
    const locationData = await getLocationData(placeId);
    return locationData;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch location data');
  }
});

// Helper function to extract unique countries from locations
const extractUniqueCountries = (locations: LocationData[]): string[] => {
  const countries = locations
    .map(location => location.country)
    .filter((country): country is string => !!country);

  return [...new Set(countries)];
};

// Slice
const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch user locations
    builder.addCase(fetchUserLocations.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserLocations.fulfilled, (state, action) => {
      state.isLoading = false;
      state.locations = action.payload.locations;
      // Use the comprehensive country list from all trainings
      state.uniqueCountries = action.payload.allCountries;
    });
    builder.addCase(fetchUserLocations.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch training locations';
    });

    // Fetch location data
    builder.addCase(fetchLocationData.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchLocationData.fulfilled, (state, action) => {
      state.isLoading = false;
      // Add or update location in the array
      const index = state.locations.findIndex(location => location.placeId === action.payload.placeId);
      if (index >= 0) {
        state.locations[index] = action.payload;
      } else {
        state.locations.push(action.payload);
      }
      // Update unique countries
      state.uniqueCountries = extractUniqueCountries(state.locations);
    });
    builder.addCase(fetchLocationData.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch location data';
    });
  }
});

export const { clearError } = locationSlice.actions;

export default locationSlice.reducer;